import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DropdownStatusList, EmptyString, FormTypeAdd, PageSize, StatusCodeActive, StatusCodeDelete } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { addSpaceEvery4, GetStatus, removeNonNumeric } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import CopyIcon from "assets/icon/CopyIcon";
import { TenantService } from "services/TenantService";
import { useModal } from "utils/context/ModalContext";
import Pagination from "components/Input/PaginationButton";
import { MainBodyCard } from "components/Cards/MainBodyCard";
import { PagesTitle } from "components/Cards/PagesTitle";
import DataTable from "components/DataTable";
import { GridEditButton } from "components/Button/GridEditButton";
import { GridDeleteButton } from "components/Button/GridDeleteButton";
import { FilterCard } from "components/Cards/FilterCard";

export default function Tenant(){
    let navigate = useNavigate()

    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()

    const { fetchTenantList, fetchDeleteTenant, fetchCreateTenantAccessKey} = TenantService()

    // FILTER
    const [tenantName, setTenantName] = useState(EmptyString)
    const [phone, setPhone] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)

    // DATA
    const [tenantList, setTenantList] = useState([])

    // Grid
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "tenantName",
            header: "Nama Tenant"
        },
        {
            key: "idNumber",
            header: "ID Number",
            render: (row) => addSpaceEvery4(row.idNumber),
        },
        {
            key: "phone",
            header: "Phone",
            render: (row) => addSpaceEvery4(row.phone),
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    {/* <GridViewButton onClick={() => viewBtnClick(row.customerId)} size="w-3" /> */}
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.tenantId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.tenantId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]

    const handlePageChange = async (newIndex) => {
        try {
            showLoading()

            const res = await fetchTenantList(
                tenantName,
                phone,
                status,
                PageSize,
                newIndex
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setTenantList(res)
            setTotalPages(total)
            setPageIndex(newIndex)
        } finally {
            hideLoading()
        }
    }

    const [link, setLink] = useState(EmptyString)
    const [showLink, setShowLink] = useState(false)
    const [copied, setCopied] = useState(false)
    
    const tenantNameChange = (e) => {setTenantName(e.target.value.toUpperCase())}
    const phoneChange = (e) => {setPhone(removeNonNumeric(e.target.value))}
    const statusChange = (e) => {setStatus(e.value)}

    // BUTTON
    const addBtnClick = () => {
        navigate("/tenant/form?tenantId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (customerId) => {
        navigate("/tenant/form?tenantId=" + customerId + "&formType=Edit")
    }
    const deleteBtnClick = async (tenantId, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            await fetchDeleteTenant(tenantId, status)
            await handlePageChange(pageIndex)
        } finally {
            hideLoading()
        }
    }
    const tenantRegistrationLinkClick = async () => {
        try {
            showLoading()

            const res = await fetchCreateTenantAccessKey()
            if (!res) return

            const accessKey = res.data.accessKey

            setLink("https://delalakost.com/tenant-registration?accessKey=" + accessKey)
            setShowLink(true)
        } finally {
            hideLoading()
        }
    }
    const copyBtnClick = async () => {
        await navigator.clipboard.writeText(link)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[tenantName, phone, status])

    return(
        <MainBodyCard className="flex flex-col gap-2">
            <PagesTitle title='Tenant' />

            <div className="flex flex-col sm:flex-row gap-5 justify-between mt-5">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Tenant Name' width='w-full sm:w-64'>
                        <input className="input" value={tenantName} onChange={tenantNameChange} />
                    </FilterCard>

                    <FilterCard title='Phone' width='w-full sm:w-64'>
                        <input className="input" value={phone} onChange={phoneChange} />
                    </FilterCard>

                    <FilterCard title='Status' width='w-full sm:w-32'>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={statusChange} placeholder={"All"} />
                    </FilterCard>
                </div>
            </div>

            <div className="mt-4 flex w-full justify-end gap-2">
                <button className="bg-black px-4 py-2 rounded-md" onClick={tenantRegistrationLinkClick}>
                    <span className="text-white">Tenant Registration Link</span>
                </button>
                <AddButton onClick={addBtnClick} title='Tambah Tenant' />
            </div>

            {showLink && (
                <div className="w-full flex justify-center items-center bg-slate-300 rounded-md py-2 px-2 mt-4">
                    <div className="w-full flex">
                        <span>{link}</span>
                    </div>
                    <button className="bg-black px-4 py-2 rounded-md flex gap-2 items-center" onClick={copyBtnClick}>
                        <CopyIcon className="fill-white w-6" />
                        <span className="text-white">Copy</span>
                    </button>
                </div>
            )}

            {copied && (
                <div>
                    <span>Link copied!</span>
                </div>
            )}
           
            <div className="mt-5 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={tenantList?.data?.listData ?? []}
                />
            </div>

            <div className="mt-5 sm:hidden flex flex-col gap-2">
                {tenantList?.data?.listData?.map((data, i) => {
                    return(
                        <div className="border rounded-md hover:bg-slate-100 bg-white py-2 px-2 flex justify-between" key={ i }>
                            <div className="text-sm">
                                <div>
                                    <span>{ data.tenantName.toUpperCase() }</span>
                                </div>                                
                                <div>
                                    <span className="">{ data.phone === EmptyString ? "-" : data.phone }</span>
                                </div>
                                <div className="mt-1 flex gap-2">
                                    <button className="bg-black px-3 py-1 rounded-md flex gap-2" onClick={() => editBtnClick(data.tenantId)}>
                                        <EditIcon className="w-4 fill-white" />
                                        <span className="text-white">Edit</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.tenantId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                        <span className="text-white">Delete</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.tenantId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                        <span className="text-white">Undelete</span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col text-right text-sm">
                                <div>
                                    <span>{ GetStatus(data.status) }</span>
                                </div>
                                <div>
                                    <span>{  }</span>
                                </div>
                                <div className="flex h-full items-center justify-end">
                                    <span>{  }</span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}